import { t } from '@/i18n';
import { DayjsSchema, jsonType, sortOrderType } from '@/utils';
import { z } from 'zod';

/// ////////////////////////////////////////////
/// enum
/// ////////////////////////////////////////////

export const treeNodeType = z.enum(['activity', 'indicator']);
export const TreeNodeTypeLabelModifier = {
  activity: t('activity'),
  indicator: t('indicator'),
} as const satisfies Record<z.infer<typeof treeNodeType>, string>;

export const indicatorType = z.enum(['output', 'outcome']);
export type IndicatorType = z.infer<typeof indicatorType>;
export const IndicatorTypeLabelModifier = {
  output: t('indicator-type-output'),
  outcome: t('indicator-type-outcome'),
} as const satisfies Record<IndicatorType, string>;

const termType = z.enum(['short', 'medium', 'long']);
export type TermType = z.infer<typeof termType>;
export const TermTypeLabelModifier = {
  short: t('short-term'),
  medium: t('medium-term'),
  long: t('long-term'),
} as const satisfies Record<TermType, string>;

const evaluationDirectionType = z.enum(['upward', 'downward']);
export type EvaluationDirectionType = z.infer<typeof evaluationDirectionType>;
const _EvaluationDirectionTypeLabelModifier = {
  upward: t('upward'),
  downward: t('downward'),
} as const satisfies Record<EvaluationDirectionType, string>;

export const dataNatureType = z.enum(['quantitative', 'qualitative']);
const _DataNatureTypeLabelModifier = {
  qualitative: t('qualitative'),
  quantitative: t('quantitative'),
} as const satisfies Record<z.infer<typeof dataNatureType>, string>;

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

export const IndicatorModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  priorId: z.string().uuid().nullable(),
  levelByPrior: z.number().int(),
  upperId: z.string().uuid().nullish(),
  levelByUpper: z.number().int(),
  title: z.string().min(1),
  key: z.string().nullable(),
  json: jsonType.nullable(),
  fiscalYear: z.number().int(),
  type: indicatorType,
  description: z.string().nullish(),
  targetValue: z.number().nullable(),
  actualValue: z.number().nullish(),
  achievementRate: z.number().nullable(),
  unit: z.string().max(20).nullable(),
  evaluationDirection: evaluationDirectionType.nullish(),
  reason: z.string().nullish(),
  noTypeReason: z.string().nullish(),
  term: termType.nullable(),
  sortOrder: sortOrderType.nullable(),
  dataNatureType: dataNatureType.nullable(),
  statusOfAchievementWhenQualitative: z.string().nullable(),
  isTargetYear: z.boolean(),
});

export const IndicatorsModel = IndicatorModel.array();

const ActivityBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  sortOrder: sortOrderType,
  activityContent: z.string().min(1),
  projectId: z.string().uuid(),
  unit: z.string().max(20).nullable(),
  actualValue: z.number().nullable(),
  previousYearActualValue: z.number().nullable(),
});

export const ActivityModel = ActivityBaseModel;
export const ActivitiesModel = ActivityBaseModel.array();

const ActivityNodePositionModel = z.object({
  x: z.number(),
  y: z.number(),
});

export const ActivityFlowNodeModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  position: ActivityNodePositionModel,
  activityId: z.string().uuid().nullable(),
  activity: ActivityModel.nullable(),
  indicatorId: z.string().uuid().nullable(),
  indicator: IndicatorModel.nullable(),
  projectId: z.string().uuid(),
});

export const ActivityFlowEdgeModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),

  // This means ActivityNode's id!
  // Not an activity, or indicator id!
  sourceNodeId: z.string().uuid(),
  targetNodeId: z.string().uuid(),

  label: z.string().nullable(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullable(),
  projectId: z.string(),
});

export const ActivityFlowModel = z.object({
  nodes: ActivityFlowNodeModel.array(),
  edges: ActivityFlowEdgeModel.array(),
});

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

const _IndicatorMutationParamsSchema = IndicatorModel.pick({
  title: true,
  upperId: true,
  type: true,
  reason: true,
  noTypeReason: true,
  sortOrder: true,
  evaluationDirection: true,
})
  .extend({
    fiscalYear: DayjsSchema(),
    description: z.string(),
  })
  .merge(
    IndicatorModel.pick({
      priorId: true,
      targetValue: true,
      term: true,
      unit: true,
      dataNatureType: true,
    }).partial(),
  );

export const IndicatorFormSchema = z.object({
  // fiscalYear: DayjsSchema(),
  type: indicatorType,
  description: z.string(),
  unit: z.string().nullish(),
  reason: z.string().nullish(),
  noTypeReason: z.string().nullish(),
});

export const UpdateConsecutiveIndicatorFormSchema = IndicatorModel.pick({
  title: true,
  type: true,
  term: true,
  unit: true,
  dataNatureType: true,
  statusOfAchievementWhenQualitative: true,
}).extend({
  annualValues: IndicatorModel.pick({
    targetValue: true,
    actualValue: true,
    achievementRate: true,
  })
    .extend({
      id: z.string().uuid().optional(),
      year: z.number().int(),
      isManualAchievementRate: z.boolean().optional(),
    })
    .array(),
});

export const UpdateConsecutiveIndicatorRequestSchema =
  UpdateConsecutiveIndicatorFormSchema.extend({
    baseIndicatorId: z.string().uuid().optional(),
  });

export const ActivityFormSchema = ActivityBaseModel.pick({
  activityContent: true,
  sortOrder: true,
  unit: true,
}).extend({
  actualValue: z.number().nullable().optional(),
  previousYearActualValue: z.number().nullable().optional(),
});

export const CreateActivityParamsSchema = ActivityFormSchema.extend({
  projectId: z.string().uuid(),
});

export const UpdateActivityParamsSchema = ActivityFormSchema.extend({
  projectId: z.string().uuid(),
  id: z.string(),
});

export const ActivityTreeNodeFormSchema = z.object({
  nodeType: treeNodeType,
  sourceActivityId: z.string().uuid().nullish(),
  sourceIndicatorId: z.string().uuid().nullish(),
  targetIndicatorId: z.string().uuid(),
  label: z.string().nullish(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullish(),
});

export const ActivityTreeNodeFormValidationSchema = z.discriminatedUnion(
  'nodeType',
  [
    ActivityTreeNodeFormSchema.extend({
      nodeType: z.literal('activity'),
      sourceActivityId: z.string().uuid(),
      sourceIndicatorId: z.string().uuid().nullish(),
    }),
    ActivityTreeNodeFormSchema.extend({
      nodeType: z.literal('indicator'),
      sourceActivityId: z.string().uuid().nullish(),
      sourceIndicatorId: z.string().uuid(),
    }),
  ],
);

const _UpdateActivityTreeSchema = z.object({
  projectId: z.string().uuid(),
  nodes: ActivityFlowNodeModel.pick({ id: true, position: true }).array(),
  edges: ActivityFlowEdgeModel.pick({
    id: true,
    sourceNodeId: true,
    targetNodeId: true,
    label: true,
  }).array(),
});

const _CreateTreeNodeRequestSchema = z.object({
  projectId: z.string().uuid(),
  sourceObject: z.object({
    activityId: z.string().uuid().nullable(),
    indicatorId: z.string().uuid().nullable(),
  }),
  targetObject: z.object({
    activityId: z.null(),
    indicatorId: z.string().uuid(),
  }),
  label: z.string().nullable(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullable(),
});

const _DeleteTreeEdgeRequestSchema = z.object({
  projectId: z.string().uuid(),
  id: z.string().uuid(),
});

const _UpdateTreeEdgeRequestSchema = z.object({
  projectId: z.string().uuid(),
  id: z.string().uuid(),
  label: z.string().nullable(),
  reasonWhyOutcomesCannotBeSetAtMultipleLevels: z.string().nullable(),
});
